import { useDataFetchingClient, useQuery } from 'data-fetching-client';
import Products from 'merchandise-lib/constants/Product';
import { useCallback, useMemo } from 'react';
import { GET_PRODUCTS_QUERY } from '../../../../__generated__/chirp/com/hubspot/buying/rpc/ProductRpc.dfc.new';
import { GET_PRODUCTS_QUERY as GET_PRODUCTS_QUERY_NO_AUTH } from '../../../../__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc.dfc.new';
import { logApiError, logApiSuccess } from '../../utils/apiLogging';
import { isValidProductLine, isValidSkuCategoryName } from '../../utils/typeguards';
import { useIsInAppAuth } from '../user/useUserInfo';
const useProductDetailsQuery = (isAppAuth = false) => isAppAuth ? GET_PRODUCTS_QUERY : GET_PRODUCTS_QUERY_NO_AUTH;
export const useProductsQuery = ({
  productApiNames,
  skip = false
} = {}) => {
  const isAppAuth = useIsInAppAuth();
  const version = 'v2';
  const query = useProductDetailsQuery(isAppAuth);
  const queryName = isAppAuth ? 'GET_PRODUCTS_QUERY' : 'GET_PRODUCTS_QUERY_NO_AUTH';
  const handleCompleted = useCallback(() => {
    logApiSuccess({
      queryName,
      operation: 'getProducts',
      version
    });
  }, [queryName, version]);
  const handleError = useCallback(error => {
    logApiError(error, {
      queryName,
      operation: 'getProducts',
      version
    });
  }, [queryName, version]);
  const {
    data,
    loading,
    error
  } = useQuery(query, {
    variables: {
      productApiNames: productApiNames && productApiNames.length > 0 ? productApiNames : []
    },
    skip: skip || !productApiNames || productApiNames.length === 0,
    onCompleted: handleCompleted,
    onError: handleError
  });
  return {
    productsByName: data === null || data === void 0 ? void 0 : data.result.productsByName,
    loading,
    error
  };
};
export const getIndividualProductDetailsFromProductsQuery = ({
  productApiName,
  productsByName
}) => {
  return productsByName === null || productsByName === void 0 ? void 0 : productsByName[productApiName];
};
export const useProductDetails = ({
  productApiName
} = {}) => {
  const productsQuery = useProductsQuery({
    productApiNames: productApiName ? [productApiName] : [],
    skip: !productApiName
  });
  if (!productApiName) return {
    productDetails: undefined,
    loading: false,
    error: undefined
  };
  return {
    productDetails: getIndividualProductDetailsFromProductsQuery({
      productApiName,
      productsByName: productsQuery === null || productsQuery === void 0 ? void 0 : productsQuery.productsByName
    }),
    loading: productsQuery.loading,
    error: productsQuery.error
  };
};
export const useIsCoreSeatSku = ({
  productApiName
}) => {
  const {
    productDetails
  } = useProductDetails({
    productApiName
  });
  return (productDetails === null || productDetails === void 0 ? void 0 : productDetails.skuCategoryName) === 'CORE_PLATFORM_SEATS';
};
export const useProductDetailsForArray = ({
  productApiNames,
  skip
}) => {
  const isAppAuth = useIsInAppAuth();
  const version = 'v2';
  const query = useProductDetailsQuery(isAppAuth);
  const queryName = isAppAuth ? 'GET_PRODUCTS_QUERY' : 'GET_PRODUCTS_QUERY_NO_AUTH';
  const client = useDataFetchingClient();
  const cachedProducts = useMemo(() => {
    try {
      var _cached$result$produc, _cached$result;
      const cached = client.readQuery({
        query,
        variables: {
          productApiNames: []
        }
      });
      return (_cached$result$produc = cached === null || cached === void 0 || (_cached$result = cached.result) === null || _cached$result === void 0 ? void 0 : _cached$result.productsByName) !== null && _cached$result$produc !== void 0 ? _cached$result$produc : {};
    } catch (_unused) {
      return {};
    }
  }, [client, query]);
  const productsToFetch = useMemo(() => {
    return productApiNames.filter(name => !(name in cachedProducts));
  }, [productApiNames, cachedProducts]);
  const handleCompleted = useCallback(() => {
    logApiSuccess({
      queryName,
      operation: 'getProducts',
      version
    });
  }, [queryName, version]);
  const handleError = useCallback(error => {
    logApiError(error, {
      queryName,
      operation: 'getProducts',
      version
    });
  }, [queryName, version]);
  const {
    data,
    loading,
    error
  } = useQuery(query, {
    variables: {
      productApiNames: productsToFetch
    },
    skip: skip || productsToFetch.length === 0,
    onCompleted: handleCompleted,
    onError: handleError
  });
  const productDetails = useMemo(() => {
    var _data$result$products, _data$result;
    if (loading || error) {
      return undefined;
    }
    const allProductsByName = Object.assign({}, cachedProducts, (_data$result$products = data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 ? void 0 : _data$result.productsByName) !== null && _data$result$products !== void 0 ? _data$result$products : {});
    const productsByName = {};
    for (const productApiName of productApiNames) {
      if (productApiName in allProductsByName) {
        productsByName[productApiName] = allProductsByName[productApiName];
      }
    }
    return {
      productsByName
    };
  }, [data, cachedProducts, productApiNames, loading, error]);
  return {
    productDetails,
    loading,
    error
  };
};
export const useHasProductInProductDetails = ({
  productApiName
}) => {
  const {
    productDetails,
    loading,
    error
  } = useProductDetails({
    productApiName
  });
  return {
    hasProduct: !!productDetails,
    loading,
    error
  };
};
export const useHasAnyCoreSeatsInProductDetails = () => {
  var _productDetails$produ;
  const {
    productDetails,
    loading
  } = useProductDetailsForArray({
    productApiNames: [Products.CORE_SEATS_STARTER, Products.CORE_SEATS_PROFESSIONAL, Products.CORE_SEATS_ENTERPRISE]
  });
  const hasAnyCoreSeats = Object.keys((_productDetails$produ = productDetails === null || productDetails === void 0 ? void 0 : productDetails.productsByName) !== null && _productDetails$produ !== void 0 ? _productDetails$produ : {}).length > 0;
  return {
    hasAnyCoreSeats,
    loading
  };
};
export const useHasAnySuiteBundlesInProductDetails = () => {
  var _productDetails$produ2;
  const {
    productDetails,
    loading
  } = useProductDetailsForArray({
    productApiNames: [Products.CRM_SUITE_STARTER_BUNDLE, Products.CRM_SUITE_PROFESSIONAL_BUNDLE, Products.CRM_SUITE_ENTERPRISE_BUNDLE]
  });
  const hasAnySuiteBundles = Object.keys((_productDetails$produ2 = productDetails === null || productDetails === void 0 ? void 0 : productDetails.productsByName) !== null && _productDetails$produ2 !== void 0 ? _productDetails$produ2 : {}).length > 0;
  return {
    hasAnySuiteBundles,
    loading
  };
};
export const useBundledSkuCategoryNamesFromApiNames = ({
  productApiNames
}) => {
  const {
    productDetails,
    loading,
    error
  } = useProductDetailsForArray({
    productApiNames
  });
  const {
    bundledSkuCategoryNames,
    bundledProductLines
  } = useMemo(() => {
    var _productDetails$produ3;
    return Object.values((_productDetails$produ3 = productDetails === null || productDetails === void 0 ? void 0 : productDetails.productsByName) !== null && _productDetails$produ3 !== void 0 ? _productDetails$produ3 : {}).reduce((acc, product) => {
      if (product.productLevel === 'FREE') {
        acc.bundledSkuCategoryNames.push(...['MARKETING_PRODUCTS', 'SALES_PRODUCTS', 'SERVICE_HUB_PRODUCTS', 'CMS_HUB_PRODUCTS', 'OPERATIONS_HUB_PRODUCTS']);
        acc.bundledProductLines.push(...['MARKETING', 'SALES', 'SERVICE', 'CMS', 'OPERATIONS']);
        return acc;
      }
      product.bundledProducts.forEach(bundledProduct => {
        if (isValidSkuCategoryName(bundledProduct.skuCategoryName)) {
          acc.bundledSkuCategoryNames.push(bundledProduct.skuCategoryName);
        }
        if (isValidProductLine(bundledProduct.productLine)) {
          acc.bundledProductLines.push(bundledProduct.productLine);
        }
      });
      return acc;
    }, {
      bundledSkuCategoryNames: [],
      bundledProductLines: []
    });
  }, [productDetails]);
  return {
    bundledSkuCategoryNames,
    bundledProductLines,
    loading,
    error
  };
};